import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, Calendar, ArrowRight, User, TrendingUp } from 'lucide-react';
import { blogPostsData } from '../data/blogPostsData';
import InquiryModal from '../components/InquiryModal';

const Blogs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');

  const handleButtonClick = (context) => {
    setButtonContext(context);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#6A0572] via-[#8B1587] to-[#6A0572] py-24 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFD700] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#AB2346] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block mb-6">
            <span className="text-[#FFD700] text-sm font-bold uppercase tracking-widest px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border-2 border-[#FFD700]/50">
              Expert Insights
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight tracking-tight">
            IP Strategies That
            <br />
            <span className="text-[#FFD700] drop-shadow-[0_4px_20px_rgba(255,215,0,0.6)]">Drive Real Results</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-100 max-w-4xl mx-auto leading-relaxed font-light">
            Proven strategies, real case studies, and actionable insights from protecting 10,000+ brands. Learn how successful businesses leverage IP for competitive advantage.
          </p>
        </div>
      </section>

      {/* All Blogs Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-[#6A0572] mb-4 tracking-tight">Latest Articles</h2>
            <p className="text-xl text-gray-600">Expert insights on IP protection and business growth</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPostsData.map((post) => (
              <a 
                href={`/blogs/${post.id}`} 
                key={post.id}
                className="block"
              >
                <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-gray-200 hover:border-[#FFD700] bg-white h-full cursor-pointer">
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#6A0572] to-[#AB2346] text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-lg">
                        {post.category}
                      </span>
                    </div>
                    {post.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="inline-block px-3 py-1.5 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#6A0572] text-xs font-black rounded-full uppercase tracking-wider shadow-lg">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 flex flex-col">
                    <h3 className="text-xl font-black text-[#6A0572] mb-4 group-hover:text-[#AB2346] transition-colors leading-tight min-h-[64px]">
                      {post.title}
                    </h3>
                    <p className="text-gray-700 mb-6 leading-relaxed text-sm line-clamp-3 flex-grow">
                      {post.excerpt}
                    </p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {post.tags.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className="text-xs font-semibold px-3 py-1 bg-[#FFD700]/20 text-[#AB2346] rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    {/* Author */}
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#6A0572] to-[#AB2346] rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-[#6A0572] text-sm">{post.author}</p>
                        <p className="text-xs text-gray-500">{post.role}</p>
                      </div>
                    </div>
                    
                    {/* Meta & CTA */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1.5 text-[#AB2346]" />
                          <span className="font-medium">{post.readTime}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1.5 text-[#AB2346]" />
                          <span className="font-medium">{post.date}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[#AB2346] font-bold text-sm group-hover:gap-3 transition-all duration-300">
                        Read More
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#6A0572] via-[#8B1587] to-[#6A0572] overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFD700] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#AB2346] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block mb-6">
            <TrendingUp className="h-12 w-12 text-[#FFD700] mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join 10,000+ businesses that have secured their competitive advantage with our expert IP protection services.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => handleButtonClick('Start Your Protection - Blogs CTA')}
              className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#e5a200] hover:to-[#FF8C00] text-[#6A0572] font-black px-10 py-6 text-lg rounded-xl shadow-2xl hover:shadow-[0_20px_60px_rgba(255,215,0,0.4)] transition-all duration-300 hover:scale-105"
            >
              Start Your Protection
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => handleButtonClick('Schedule Consultation - Blogs CTA')}
              className="border-2 border-white text-white hover:bg-white hover:text-[#6A0572] font-bold px-10 py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </section>

      <InquiryModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        buttonContext={buttonContext}
      />
    </div>
  );
};

export default Blogs;
